import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DepartmentDetailsFormService } from './department-details-form.service';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';
import {
  ConfigurationManagerService,
  ConnectionService,
  EntityManagerService,
  LoaderService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';
import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { $state } from 'app/ajs-upgraded-providers';
import { Departments, GiftcardsTypes, Vat } from 'tilby-models';
import { Subject, takeUntil } from 'rxjs';
import { DepartmentsFormGroups } from '../departments.model';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';

import {
  ItemDataCatalogService,
  ItemDataOperationsService
} from 'src/app/features/items';

import { MatSelectModule } from '@angular/material/select';
import { EncodedData, exempts, salesTypes } from 'src/app/features/items/utils/item.constants';
import { LeanPMSChainProduct } from 'src/app/features/digital-payments';
import { TrimInputDirective } from 'src/app/directives/trim.directive';

@Component({
  selector: 'app-department-details',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatButtonModule, MatSlideToggleModule,
    MatIconModule, MatSelectModule, TrimInputDirective
  ],
  templateUrl: './department-details.component.html',
  styleUrl: './department-details.component.scss'
})
export class DepartmentDetailsComponent implements OnInit, OnDestroy {

  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly connection = inject(ConnectionService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);
  private readonly loaderService = inject(LoaderService);
  private readonly state = inject($state);
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  readonly departmentFormService = inject(DepartmentDetailsFormService);
  
  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");
  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';

  @Input() department!: Departments;
  @Input() activityCodes: Array<any> = [];
  vats: Array<Vat> = [];
  giftcardsTypes: Array<GiftcardsTypes> = [];
  
  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  departmentForm!: DepartmentsFormGroups;
  chainProductsLeanPMS: Array<LeanPMSChainProduct> = [];
  salesTypesList!: Array<EncodedData>;
  giftcardsTypesList: Array<GiftcardsTypes> = [];

  isDataListReady = this.itemDataCatalogService.isReady;
  departmentFormReady = this.departmentFormService.departmentFormReady;
  
  constructor() {
    effect(() => {
      if(this.isDataListReady()) {
        this.readData();
        this.vats = this.itemDataCatalogService.getDataByKey('vat') || [];
        this.giftcardsTypes = this.itemDataCatalogService.getDataByKey("giftcards-types") || [];
        this.initLists();
        this.departmentForm = this.departmentFormService.createForm(this.department);
        this.loaderService.forceHide();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      if(this.departmentFormReady()) {
        this.listenToDepartmentNameChanges();
      }
    })
  }

  ngOnInit(): void {
    this.isNew = !this.department?.id;
    this.createToolbarButtons();
    this.initContextToolbarLabel();
  }

  initLists() {
    this.giftcardsTypesList = [...this.giftcardsTypes];
    this.giftcardsTypesList.unshift({uuid: "", name: "No", expire_after: 0, is_divisible: false});
  }

  readData() {
    this.salesTypesList = [...salesTypes];

    this.itemDataOperationsService.isLeanPmsEnabled().then((result) => {
      if(result) {
        this.readChainProduction();
      }
    })
  }

  readChainProduction() {
    this.itemDataOperationsService.fetchChainProducts().then((result) => {
      if(result) {
        this.chainProductsLeanPMS = result;
      }
    })
  }

  initContextToolbarLabel() {
    if(this.isNew) {
      this.toolbarEventsContextService.label = `ITEMS.DEPARTMENTS.DETAILS.NAME_PLACEHOLDER`;
    } else {
      // Aggiungo un carattere invisibile in coda al nome reale
      // Questo impedisce al pipe | translate di trovare una corrispondenza esatta
      this.toolbarEventsContextService.label = (this.department.name || "") + '\u200b';
    }
  }
  
  private listenToDepartmentNameChanges() {
    this.departmentFormService.name?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.toolbarEventsContextService.label = value? value + '\u200b': `ITEMS.DEPARTMENTS.DETAILS.NAME_PLACEHOLDER`;
    })
  }
  
  selectChainProductsLeanPMS() {
    this.listSelectorDialogService.openDialog({
        data: {
          optionList: this.chainProductsLeanPMS, 
          optionValue: this.chainProductsLeanPMS.find(prod => prod.id == this.departmentFormService.pms_code?.value)
        }
      }
    ).then((res: LeanPMSChainProduct | undefined) => {
      if(res) {
        this.departmentFormService.pms_code?.setValue(res.id)
      }
    })
  }

  getVatValue(vat: Vat) {
    return vat ? (vat.code ? this.translate.instant(exempts[vat.code as keyof typeof exempts] || vat.code) : vat.value + "%") : "";
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_department',
          icon: signal('data_object'),
          label: signal('ITEMS.DEPARTMENTS.ACTIONS.SHOW_JSON'),
          click: _ => this.openDepartmentJsonDetail()
        }
      ]
    })
  }
  
  async openDepartmentJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.DEPARTMENTS.ACTIONS.SHOW_JSON', 
        nameFile: 'item_json_data', 
        json: this.departmentFormService.getDepartmentFields(this.department) } 
    });
  }
  
  back() {
    this.state.go(`app.new.items.departments`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DEPARTMENTS.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
    
    this.departmentFormService.resetFormValues(this.department);
  }

  async save() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    if(this.departmentFormService.form.invalid) {
      this.departmentFormService.form.markAllAsTouched();
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DEPARTMENTS.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    const departmentToSend = {
      ...this.departmentFormService.getDepartmentFields(this.department),
      ...(this.department?.id && {id: this.department.id})
    } as Departments;

    try {
      if(departmentToSend.id) {
        this.department = await this.entityManager.departments.putOneOnline(departmentToSend);
      } else {
        this.department = await this.entityManager.departments.postOneOnline(departmentToSend);
      }

      // Aggiornamento lista ingredienti
      // const currentDepartments = this.itemDataCatalogService.getDataByKey('departments') || [];

      // if (departmentToSend.id) {
      //   const index = currentDepartments.findIndex(d => d.id === this.department.id);
      //   if (index !== -1) {
      //     currentDepartments[index] = this.department;
      //   }
      // } else {
      //   currentDepartments.push(this.department);
      // }

      // this.itemDataCatalogService.addDataToCatalog('departments', currentDepartments);

      this.state.go(`app.new.items.departments`);

      this._snackBar.open(this.translate.instant('ITEMS.DEPARTMENTS.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: departmentToSend.id ? 'ITEMS.DEPARTMENTS.ERROR_IN_EDIT' : 'ITEMS.DEPARTMENTS.ERROR_IN_CREATION'
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.departmentFormReady.set(false);
  }
}